import { brand } from "../../config/theme";
import { useNavigate, useLocation } from "react-router-dom";
import CompanyScopedOutlet from "../../components/base/CompanyScopedOutlet";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import {
  AppsSidebar,
  useFilteredCategories,
} from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { basesAppByBase } from "./config";
import { useBasesStore } from "./store";
import { useBasesLocacionesStore } from "./basesStore";
import { usePatrolsStore } from "./patrolsStore";
import { useAccess } from "../../hooks/useAccess";

// Single shell for the Amenidades & Reservas apps. Derives the active app from
// the URL ("/amenidades..." | "/reservas..."), renders its sub-menu and the
// matched section via <Outlet/>. Mirrors NuvexAccessLayout, but uses the
// LOGGED-IN user's company (no hardcoded companyId — multi-tenant).
export default function BasesLayout() {
  const { t } = useTranslation("common");
  const { t: tb } = useTranslation("bases");
  const { canAccessSectionOfApp } = useAccess();
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hydrate = useBasesStore((state) => state.hydrate);
  const hydrateBases = useBasesLocacionesStore((state) => state.hydrate);
  const hydratePatrols = usePatrolsStore((state) => state.hydrate);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const app = basesAppByBase(location.pathname);

  // Datos de la compañía por la API (CC-CONSOLE #1592): bases, personal y
  // amenidades/reservas siempre; Rondines sólo dentro de su sub app. Se
  // relee al cambiar de usuario (o de compañía).
  useEffect(() => {
    if (!user) return;
    void hydrate();
    void hydrateBases();
  }, [hydrate, hydrateBases, user]);
  useEffect(() => {
    if (!user || app.id !== "rondines") return;
    void hydratePatrols();
  }, [hydratePatrols, user, app.id]);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (item: AppItem) => {
    if (item.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: item.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (item.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: item.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (item.route) {
      closeAppsSidebar();
      navigate(item.route);
    } else if (item.externalUrl) {
      window.open(item.externalUrl, "_blank");
    }
  };

  const menuItems = app.menu
    .filter((item) => canAccessSectionOfApp(app.id, item.id))
    .map((item) => ({
    id: item.id,
    label: tb(`menu.${item.labelKey}`),
    icon: item.icon,
    path: `${app.base}/${item.id}`,
  }));

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="h-full bg-app">
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}

      <Header
        title={t("categories.bases-y-propiedades.title")}
        subtitle={t(`apps.${app.titleKey}.name`)}
        onOpenAppsSidebar={openAppsSidebar}
        isAppsSidebarOpen={isAppsSidebarOpen}
        />

      <div className="flex h-full">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) =>
            location.pathname === `${app.base}/${itemId}` ||
            location.pathname.startsWith(`${app.base}/${itemId}/`)
          }
        />

        <div className="flex-1 overflow-hidden flex justify-center w-full">
          <CompanyScopedOutlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={app.id}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
